import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SceneTimeContext, EASE } from './sceneTime';
import { Background } from '../components/Background';
import { SCENE_COMPONENTS } from '../scenes';
import { PRESENTATION } from '../presentationConfig';

export const STAGE_W = 1920;
export const STAGE_H = 1080;

const ENTER = {
  fade: { opacity: 0 },
  zoom: { opacity: 0, scale: 0.92 },
  pull: { opacity: 0, scale: 1.1 },
  rise: { opacity: 0, y: 90 },
  cut: { opacity: 0 },
};
const EXIT = {
  fade: { opacity: 0 },
  zoom: { opacity: 0, scale: 1.16 },
  pull: { opacity: 0, scale: 0.9 },
  rise: { opacity: 0, y: -70 },
  cut: { opacity: 0, transition: { duration: 0.18 } },
};

const variants = {
  enter: (c) => ({ ...(ENTER[c.enter] || ENTER.fade) }),
  center: { opacity: 1, scale: 1, y: 0, transition: { duration: PRESENTATION.transitionSeconds, ease: EASE } },
  exit: (c) => ({ ...(EXIT[c.exit] || EXIT.fade), transition: { duration: 0.8, ease: EASE, ...((EXIT[c.exit] || {}).transition || {}) } }),
};

function pad(n) { return String(n).padStart(2, '0'); }
export function fmtClock(sec) { const s = Math.max(0, Math.round(sec)); return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`; }

function HUD({ index, total, t, duration, overall, totalSeconds }) {
  const remaining = Math.max(0, Math.ceil(duration - t));
  return (
    <>
      <div className="pk-hud" data-testid="pk-hud">
        <span className="pk-hud-scene">{pad(index + 1)} / {pad(total)}</span>
        <span className="pk-hud-sep" />
        <span className="pk-hud-time">{fmtClock(remaining)}</span>
      </div>
      <div className="pk-progress-line" aria-hidden>
        <div className="pk-progress-fill" style={{ transform: `scaleX(${Math.min(1, overall / totalSeconds)})` }} />
      </div>
    </>
  );
}

// CSS zoom (not transform: scale) so text is laid out and rasterised at its final size — no jagged/blurry glyphs on scaled layers.
const SUPPORTS_ZOOM = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '1');

// Fixed 1920×1080 design surface scaled to fit its container.
export function Stage({ scenes, clock, showTimer = true, mode = 'present', audioOn = false, onClick, className = '' }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setScale(Math.min(width / STAGE_W, height / STAGE_H));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scene = scenes[clock.index];
  const Component = SCENE_COMPONENTS[scene.component];
  const key = scene.group || scene.id;
  const duration = PRESENTATION.sceneDuration;
  const totalSeconds = scenes.length * duration;

  return (
    <div ref={wrapRef} className={`pk-viewport pk-mode-${mode} ${className}`} onClick={onClick} data-testid="pk-stage-viewport">
      <div
        className={`pk-stage ${clock.playing ? '' : 'pk-paused'}`}
        style={{ width: STAGE_W, height: STAGE_H, ...(SUPPORTS_ZOOM ? { zoom: scale } : { transform: `scale(${scale})` }) }}
        data-testid="pk-stage"
        data-scene={clock.index + 1}
      >
        <Background dark={scene.dark || 0} />
        <SceneTimeContext.Provider value={{
          t: clock.t, playing: clock.playing, duration, stage: clock.stage, groupT: clock.groupT,
          tMV: clock.tMV, groupTMV: clock.groupTMV, mode, audioOn, scene,
        }}>
          <AnimatePresence mode="sync" initial={false} custom={{ enter: scene.enter, exit: scene.exit }}>
            <motion.div
              key={key}
              className="pk-scene"
              custom={{ enter: scene.enter, exit: scene.exit }}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              data-testid={`pk-scene-${scene.n}`}
            >
              <Component scene={scene} scenes={scenes} />
            </motion.div>
          </AnimatePresence>
        </SceneTimeContext.Provider>
        {showTimer && (
          <HUD index={clock.index} total={scenes.length} t={clock.t} duration={duration} overall={clock.overall} totalSeconds={totalSeconds} />
        )}
      </div>
    </div>
  );
}
